"use client";

import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { services_data } from "./ServicesStatic";

const BRAND = { navy: "#062b6c", green: "#00ad0f" };

const socials = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/Scaftechengineeringservices",
    icon: "mdi:facebook",
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/in/scaftech-engineering-services-61aaa0255/overlay/about-this-profile/",
    icon: "mdi:linkedin",
  },
  {
    name: "YouTube",
    href: "https://www.youtube.com/@scaftech_nigeria",
    icon: "mdi:youtube",
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/scaftech.ng/",
    icon: "mdi:instagram",
  },
];

const TopStrip = () => (
  <div
    className="hidden xl:flex items-center justify-between px-8 py-2 text-xs text-white"
    style={{
      background: "rgba(2,8,23,0.96)",
      borderBottom: `1px solid ${BRAND.green}33`,
      backdropFilter: "blur(14px)",
    }}
  >
    <div className="flex items-center gap-6">
      <a
        href="mailto:consult@scaftechservices.com"
        className="flex items-center gap-2 group"
      >
        <Icon
          icon="mdi:email-outline"
          className="h-4 w-4 text-[#00ad0f] group-hover:scale-110 transition-transform"
        />
        <span className="tracking-[0.14em] uppercase text-white/60">Email</span>
        <span className="font-medium text-white group-hover:text-[#7dff9a] transition-colors">
          consult@scaftechservices.com
        </span>
      </a>

      <span className="h-4 w-px bg-white/15" />

      <div className="flex items-center gap-4">
        <a href="tel:07086419457" className="flex items-center gap-2 group">
          <Icon
            icon="mdi:phone-outline"
            className="h-4 w-4 text-[#00ad0f] group-hover:scale-110 transition-transform"
          />
          <span className="text-white/60 uppercase tracking-[0.14em]">
            Lagos
          </span>
          <span className="font-medium group-hover:text-[#7dff9a] transition-colors">
            07086419457
          </span>
        </a>

        <a href="tel:09028728144" className="flex items-center gap-2 group">
          <Icon
            icon="mdi:phone-in-talk-outline"
            className="h-4 w-4 text-[#00ad0f] group-hover:scale-110 transition-transform"
          />
          <span className="text-white/60 uppercase tracking-[0.14em]">
            Port Harcourt
          </span>
          <span className="font-medium group-hover:text-[#7dff9a] transition-colors">
            09028728144
          </span>
        </a>
      </div>
    </div>

    <div className="flex items-center gap-5">
      <ul className="flex items-center gap-3">
        {socials.map((s) => (
          <li key={s.name} className="group relative">
            <a
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.name}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur-sm hover:border-[#00ad0f]/70 hover:bg-[#00ad0f]/10 transition-all"
            >
              <Icon
                icon={s.icon}
                className="h-4 w-4 text-white group-hover:scale-110 transition-transform"
              />
            </a>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-2 pl-4 border-l border-white/10">
        <div className="relative flex h-8 w-8 items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-[#00ad0f]/35 blur-[2px]" />
          <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-[#00ad0f] text-[0.6rem] font-bold tracking-[0.18em] uppercase text-white">
            ISO
          </span>
        </div>
        <div className="text-[11px] leading-tight">
          <div className="font-semibold text-white">ISO 9001</div>
          <div className="uppercase tracking-[0.16em] text-white/60">
            Certified
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MegaMenu = ({ open }) => {
  const columns = [[], [], []];
  services_data
    .filter((s) => s.type === "main-list")
    .forEach((s, i) => columns[i % 3].push(s));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
          className="absolute left-1/2 z-50 mt-3 w-[880px] -translate-x-1/2 rounded-2xl border border-[#062b6c20] bg-white/95 backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.25)] p-6"
        >
          <div className="mb-4 flex items-center justify-between text-[11px] text-slate-500">
            <span className="uppercase tracking-[0.18em] text-[#062b6c]">
              Service matrix
            </span>
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00ad0f]" />
              <span className="tracking-[0.16em] uppercase text-slate-400">
                Marine • Lifting • Inspection
              </span>
            </span>
          </div>

          <div className="grid grid-cols-3 gap-6 text-sm">
            {columns.map((col, i) => (
              <div key={i} className="space-y-4">
                {col.map((item) => {
                  const children = services_data.filter(
                    (s) => s.belongsTo === item.name
                  );
                  return (
                    <div key={item.name} className="space-y-2">
                      <a
                        href={`/services${item.url}`}
                        className="inline-flex items-center gap-2 font-semibold text-[#062b6c] hover:text-[#00ad0f] transition-colors"
                      >
                        <Icon
                          icon="mdi:hexagon-multiple-outline"
                          className="h-4 w-4 text-[#00ad0f]"
                        />
                        <span>{item.name}</span>
                      </a>
                      {children.length > 0 && (
                        <ul className="space-y-1 text-[0.82rem] text-slate-600">
                          {children.map((sub) => (
                            <li key={sub.name}>
                              <a
                                href={`/services${sub.url}`}
                                className="flex items-center gap-2 hover:text-[#00ad0f]"
                              >
                                <span className="h-1 w-1 rounded-full bg-slate-300" />
                                <span>{sub.name}</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`w-full top-0 left-0 z-50 transition-all duration-300 ${
        scrolled ? "shadow-lg bg-white/95 backdrop-blur-md" : "bg-white"
      }`}
    >
      <TopStrip />

      <div className="mx-auto flex max-w-[1250px] items-center justify-between px-6 py-3.5">
        <a href="/" className="flex items-center gap-3">
          <img
            src="/logo.webp"
            alt="Scaftech logo"
            className="h-10 w-auto md:h-12"
          />
        </a>

        {/* DESKTOP NAV */}
        <nav className="hidden flex-1 items-center justify-center lg:flex">
          <div className="inline-flex items-center gap-6 rounded-full border border-slate-200 bg-slate-50/70 px-6 py-2 shadow-sm">
            <a
              href="/"
              className="text-sm font-medium text-slate-700 hover:text-[#00ad0f] transition-colors"
            >
              Home
            </a>

            <div
              className="relative"
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#00ad0f] transition-colors"
              >
                <span>Services</span>
                <Icon
                  icon={megaOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                  className="h-4 w-4"
                />
              </button>
              <MegaMenu open={megaOpen} />
            </div>

            <div className="relative group">
              <a
                href="/who-we-are"
                className="text-sm font-medium text-slate-700 group-hover:text-[#00ad0f] transition-colors"
              >
                About Us
              </a>

              {/* Hover buffer wrapper */}
              <div className="absolute left-1/2 top-full z-40 flex -translate-x-1/2 pt-3">
                <div className="hidden w-52 rounded-xl border border-slate-100 bg-white shadow-[0_14px_40px_rgba(15,23,42,0.18)] group-hover:block">
                  <a
                    href="/who-we-are"
                    className="block px-4 py-2.5 text-sm hover:bg-slate-50"
                  >
                    Who We Are
                  </a>
                  <a
                    href="/certification"
                    className="block px-4 py-2.5 text-sm hover:bg-slate-50"
                  >
                    Award &amp; Certification
                  </a>
                  <a
                    href="/knowledge-bank"
                    className="block px-4 py-2.5 text-sm hover:bg-slate-50"
                  >
                    Knowledge Bank
                  </a>
                </div>
              </div>
            </div>

            <a
              href="/gallery"
              className="text-sm font-medium text-slate-700 hover:text-[#00ad0f] transition-colors"
            >
              Gallery
            </a>

            <a
              href="/contact-us"
              className="text-sm font-medium text-slate-700 hover:text-[#00ad0f] transition-colors"
            >
              Contact Us
            </a>
          </div>
        </nav>

        {/* DESKTOP CTAs + MOBILE TOGGLE */}
        <div className="flex items-center gap-2 md:gap-3">
          <a
            href="/verify-certificate"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#062b6c]/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#062b6c] hover:bg-[#062b6c] hover:text-white transition-colors"
          >
            <Icon icon="mdi:shield-check-outline" className="h-4 w-4" />
            <span>Verify</span>
          </a>

          {/* NEW: Academy */}
          <a
            href="https://academy.scaftechservices.com"
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#00ad0f]/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#00ad0f] hover:bg-[#00ad0f] hover:text-white transition-colors"
          >
            <Icon icon="mdi:school-outline" className="h-4 w-4" />
            <span>Academy</span>
          </a>

          <a
            href="/get-quote"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-[#00ad0f] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-[0_0_18px_rgba(0,173,15,0.6)] hover:shadow-[0_0_26px_rgba(0,173,15,0.8)] hover:-translate-y-[1px] transition-all"
          >
            <Icon icon="mdi:clipboard-text-outline" className="h-4 w-4" />
            <span>Request Quote</span>
          </a>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border border-[#062b6c] p-2 text-[#062b6c] hover:bg-[#062b6c] hover:text-white lg:hidden transition-colors"
            onClick={() => setMobileOpen(true)}
          >
            <Icon icon="mdi:menu" className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* MOBILE SHEET */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[1000] flex bg-black/40"
            onClick={() => setMobileOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="ml-auto flex h-full w-full max-w-xs flex-col bg-white p-6 shadow-2xl"
              initial={{ x: 260 }}
              animate={{ x: 0 }}
              exit={{ x: 260 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-6 flex items-center justify-between">
                <img
                  src="/logo.webp"
                  alt="Scaftech logo"
                  className="h-10 w-auto"
                />
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-full p-2 hover:bg-slate-100"
                >
                  <Icon icon="mdi:close" className="h-5 w-5" />
                </button>
              </div>

              <nav>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a
                      href="/"
                      className="block rounded-md px-2 py-3 font-semibold hover:bg-slate-50"
                    >
                      Home
                    </a>
                  </li>
                  <li>
                    <details className="rounded-md open:bg-slate-50/60">
                      <summary className="cursor-pointer px-2 py-3 font-semibold hover:bg-slate-50">
                        Services
                      </summary>
                      <div className="mt-2 space-y-3 pl-4 pb-3">
                        {services_data
                          .filter((s) => s.type === "main-list")
                          .map((s) => (
                            <div key={s.name}>
                              <a
                                href={`/services${s.url}`}
                                className="block text-sm font-medium text-[#062b6c]"
                              >
                                {s.name}
                              </a>
                              {services_data
                                .filter((sub) => sub.belongsTo === s.name)
                                .map((sub) => (
                                  <a
                                    key={sub.name}
                                    href={`/services${sub.url}`}
                                    className="mt-1 block pl-3 text-[0.82rem] text-slate-700 hover:text-[#00ad0f]"
                                  >
                                    {sub.name}
                                  </a>
                                ))}
                            </div>
                          ))}
                      </div>
                    </details>
                  </li>
                  <li>
                    <a
                      href="/who-we-are"
                      className="block rounded-md px-2 py-3 font-semibold hover:bg-slate-50"
                    >
                      About Us
                    </a>
                  </li>
                  <li>
                    <a
                      href="/gallery"
                      className="block rounded-md px-2 py-3 font-semibold hover:bg-slate-50"
                    >
                      Gallery
                    </a>
                  </li>
                  <li>
                    <a
                      href="/contact-us"
                      className="block rounded-md px-2 py-3 font-semibold hover:bg-slate-50"
                    >
                      Contact Us
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://academy.scaftechservices.com"
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 block rounded-md border border-[#00ad0f] px-4 py-3 text-center font-semibold text-[#00ad0f] hover:bg-[#00ad0f] hover:text-white transition-colors"
                    >
                      Academy
                    </a>
                  </li>
                  <li>
                    <a
                      href="/get-quote"
                      className="mt-3 block rounded-md bg-[#00ad0f] px-4 py-3 text-center font-bold text-white hover:scale-[1.02] transition-transform"
                    >
                      Request Quote
                    </a>
                  </li>
                  <li>
                    <a
                      href="/verify-certificate"
                      className="mt-2 block rounded-md bg-[#062b6c] px-4 py-3 text-center font-semibold text-white hover:scale-[1.02] transition-transform"
                    >
                      Verify Certificate
                    </a>
                  </li>
                </ul>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
